import Vue from 'vue'
import App from './App.vue'
import store from './store'
import Router from 'vue-router';
import {getUserInformation} from "@/endpoints";
import {paths} from "@/path";
import { Permissions } from "./types"
import NotFoundPage from "./components/pages/NotFound.vue";

Vue.config.productionTip = false;

Vue.use(Router);

/**
 * Iterates over permissions derived from authenticated context and checks to see if target permission exists.
 *
 * @param {string} permission
 * @returns {boolean}
 */
const hasPermission = async (permission: string) => {

    if(store.state.authenticatedContext === null) {
        store.commit('setUser', await getUserInformation())
    }

    const permissions = store.state.authenticatedContext.permissions.map(permission => { return permission.name });

    return permissions.indexOf(permission) !== -1 || permissions.indexOf(Permissions.FULL_ACCESS) !== -1;
};

const guardedRoute = async (permission: string, next: Function) => {
    if(!await hasPermission(permission)) {
        if(store.state.authenticatedContext.user === null) {
            next(paths.login);
        } else {
            next(paths.not_found);
        }
    } else {
        next();
    }
};

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        {
            path: "/",
            name: "Index",
            redirect: paths.map
        },
        {
            path: paths.dealership,
            name: 'Dealership Page',
            component: () => import(/* webpackChunkName: "dealership" */ './components/pages/Dealership.vue'),
            beforeEnter: async (to, from, next) => {
                // TODO Allow anonymous viewing of dealership?
                await hasPermission(null);
                next();
            }
        },
        {
            path: paths.map,
            name: 'Dealership Map',
            // Lazy load
            component: () => import(/* webpackChunkName: "map" */ './components/pages/Map.vue'),
            beforeEnter: async (to, from, next) => {
                guardedRoute(Permissions.VIEW_MAP, next);
            }
        },
        {
            path: paths.login,
            name: 'Login',
            // Lazy load
            component: () => import(/* webpackChunkName: "login" */ './components/pages/Login.vue'),
            beforeEnter: async (to, from, next) => {
                // Not really interested in the result - we just want to initialize the user context before
                // continuing
                await hasPermission(null);

                store.state.authenticatedContext.user !== null ? next("/") : next();
            }
        },
        {
            path: paths.logs,
            name: 'Vehicle Logs',
            // Lazy load
            component: () => import(/* webpackChunkName: "vehicle_logs" */ './components/pages/VehicleHistoryLogs.vue'),
            beforeEnter: async (to, from, next) => {
                guardedRoute(Permissions.VIEW_VEHICLE_HISTORY_LOGS, next);
            }
        },
        {
            path: paths.entity_manager,
            name: 'Entity Manager',
            // Lazy load
            component: () => import(/* webpackChunkName: "entity_manager" */ './components/pages/EntityManager.vue'),
            beforeEnter: async (to, from, next) => {
                guardedRoute(Permissions.ADD_EDIT_DEALERSHIPS, next);
            }
        },
        {
            path: paths.users,
            name: 'Users',
            // Lazy load
            component: () => import(/* webpackChunkName: "users" */ './components/pages/Users.vue'),
            beforeEnter: async (to, from, next) => {
                guardedRoute(Permissions.ADD_EDIT_USERS, next);
            }
        },
        {
            path: paths.application,
            name: 'Application',
            component: () => import(/* webpackChunkName: "application" */ './components/pages/Application.vue'),
            beforeEnter: async (to, from, next) => {
                guardedRoute(Permissions.APPLICATION_ACCESS, next);
            }
        },
        {
            path: paths.operations,
            name: 'Operations',
            component: () => import(/* webpackChunkName: "operations" */ './components/pages/Operations.vue'),
            beforeEnter: async (to, from, next) => {
                guardedRoute(Permissions.APPLICATION_ACCESS, next);
            }
        },
        /*{
            path: paths.crawler,
            name: 'Crawler',
            component: () => import('./components/pages/Crawler.vue'),
            beforeEnter: async (to, from, next) => {
                if(await isAuthenticated()) {
                    next();
                } else {
                    next(paths.login);
                }

            }
        },*/
        {
            path: "*",
            name: "NotFound",
            component: NotFoundPage,
            beforeEnter: async (to, from, next) => {
                // Not really interested in the result - we just want to initialize the user context before
                // continuing
                await hasPermission(null);

                store.state.authenticatedContext.user === null ? next(paths.login) : next();
            }
        }
    ]
});

new Vue({
    router,
    store,
    render: h => h(App),
    provide: {
        hasPermission
    }
}).$mount('#app');
